// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import { Button } from 'moods-react/Button';
import TemplateSelectionTiles from '../template-selection-tiles/TemplateSelectionTilesComponent';
import Search from '../../../common/search/SearchComponent';
import { getProductTypeIntlPluralKey } from '../../../services/product-type/ProductTypeService';
import { getProductIdByRoute } from '../../../services/products-route/ProductsRouteService';
import { RoutesHelper } from '../../../../routes/RoutesHelper';
import { canCreateProductByFullUpload } from '../../../services/user-permission/UserPermissionService';
import commonMessages from '../../../../data/common';
import creationMessages from '../../../../data/product-creation';
import type { ProductTemplates } from '../../../../api/types/ProductTemplates';
import type { Request } from '../actions/templates/TemplatesActions';
import AnalyticsClient from '../../../../clients/AnalyticsClient';
import productEvents from '../../../../analytics/events/products';

import './TemplateSelection.scss';

export type TemplateSelectionActions = {
  clearCreationTemplates: () => void,
  requestCreationTemplates: (Request) => void,
  searchTemplatesAction: (string) => void,
};

type DefaultProps = {
  isSingleProductType: boolean,
  templateSearch: string,
  isLoading: boolean,
  hasMore: boolean,
  total: number,
  params: {
    type: ?string,
  },
};

export type TemplateSelectionProps = {
  templates: Array<ProductTemplates>,
  templateSearch: string,
  isLoading: boolean,
  hasMore: boolean,
  runtimeConfig: {
    api: string,
  },
  config: Object,
  start: number,
  total: number,
  actions: TemplateSelectionActions,
  params: {
    type: ?string,
  },
};

/**
 * Page for selecting a template for the Creation Flow
 */
class TemplateSelectionComponent extends React.Component<TemplateSelectionProps> {
  static displayName = 'TemplateSelection';

  static propTypes = {
    templates: PropTypes.array.isRequired,
    templateSearch: PropTypes.string,
    isLoading: PropTypes.bool,
    hasMore: PropTypes.bool,
    runtimeConfig: PropTypes.shape({
      api: PropTypes.string.isRequired,
    }),
    start: PropTypes.number,
    total: PropTypes.number,
    actions: PropTypes.shape({
      clearCreationTemplates: PropTypes.func.isRequired,
      requestCreationTemplates: PropTypes.func.isRequired,
    }),
  };

  static contextTypes = {
    intl: intlShape.isRequired,
  };

  static defaultProps: DefaultProps = {
    isSingleProductType: false,
    templateSearch: '',
    isLoading: false,
    hasMore: false,
    total: 0,
    params: {
      type: null,
    },
  };

  searchTemplate = (searchTerm: string): void => {
    const productTypeId = getProductIdByRoute(this.props.match.params.type);

    this.props.actions.clearCreationTemplates();
    this.props.actions.searchTemplatesAction(searchTerm);
    this.props.actions.requestCreationTemplates({
      productTypeId,
      searchTerm,
      apiUrl: this.props.runtimeConfig.api,
    });
    // Only send the GA event if the searchTerm is not blank (i.e. do not track if clearing the search)
    if (searchTerm.length !== 0) {
      AnalyticsClient.trackEvent(productEvents.searchTemplate());
    }
  };

  loadMoreTemplates = (): void => {
    const productTypeId = getProductIdByRoute(this.props.match.params.type);

    this.props.actions.requestCreationTemplates({
      productTypeId,
      searchTerm: this.props.templateSearch,
      start: this.props.start,
      apiUrl: this.props.runtimeConfig.api,
    });
  };

  templateResults() {
    const productTypeId = getProductIdByRoute(this.props.match.params.type);

    if (!productTypeId) {
      return '';
    }

    const productTypeName = this.context.intl.formatMessage(getProductTypeIntlPluralKey(productTypeId));

    return `${productTypeName} (${this.props.total})`;
  }

  getFooter() {
    const fullUploadPath = RoutesHelper.fullUploadByType(this.props.match.params.type);

    return !canCreateProductByFullUpload(this.props.config.permissions) ? (
      ''
    ) : (
      <div className="template-selection-component__footer">
        <div className="template-selection-component__footer-content">
          <div className="template-selection-component__footer-text">
            <div className="template-selection-component__footer-header">
              <h3>Do you have a print-ready design file?</h3>
            </div>
            <div className="template-selection-component__footer-subheader">We accept JPG, PDF, PNG and GIF files.</div>
          </div>
          <div className="template-selection-component__footer-btn">
            <Button onClick={() => this.props.history.push(fullUploadPath)}>Upload your own design</Button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { hasMore } = this.props;
    const { formatMessage } = this.context.intl;
    return (
      <div className="template-selection-component">
        <div className="template-selection-component__content">
          <div className="template-selection-component__content-title u-fontFamily-primaryMedium">
            {formatMessage(creationMessages.selectTemplateTitle)}
          </div>
          <div className="template-selection-component__content-toolbar">
            <div className="template-selection-component__content-toolbar__results u-fontFamily-secondary">
              {this.templateResults()}
            </div>
            <div className="template-selection-component__content-toolbar__search">
              <Search
                value={this.props.templateSearch}
                onSearchCallback={this.searchTemplate}
                placeholder={formatMessage(commonMessages.searchBarPlaceholder)} // Search
              />
            </div>
          </div>
          <TemplateSelectionTiles
            isLoading={this.props.isLoading}
            templates={this.props.templates}
            hasMore={hasMore}
            loadMore={this.loadMoreTemplates}
          />
        </div>
        {this.getFooter()}
      </div>
    );
  }
}

export default TemplateSelectionComponent;
